import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ExpenseTypeService } from 'src/app/expenses/services/expense-type.service';
import { ExpenseTypeKind, IExpenseType } from 'src/app/expenses/models/expense-type';

@Component({
    selector: 'app-expense-types',
    templateUrl: './expense-types.component.html',
    standalone: false
})
export class ExpenseTypesComponent implements OnInit {

  expenseTypes: IExpenseType[] = [];
  formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;
  private typeChangeSub?: Subscription;

  ExpenseTypeKind = ExpenseTypeKind;
  kindOptions = [
    { text: 'KmExpense', value: ExpenseTypeKind.KmExpense },
    { text: 'CashExpense', value: ExpenseTypeKind.CashExpense }
  ];

  constructor(private expenseTypeService: ExpenseTypeService) { }

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.expenseTypeService.searchExpenseTypes().subscribe(response => {
      this.expenseTypes = response.data;
    });
  }

  kindName(type: ExpenseTypeKind): string {
    return ExpenseTypeKind[type];
  }

  addHandler({ sender }) {
    this.closeEditor(sender);
    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(null, Validators.required),
      type: new UntypedFormControl(ExpenseTypeKind.KmExpense, Validators.required),
      kmRate: new UntypedFormControl(null),
      flatrate: new UntypedFormControl(null),
      salaryPayment: new UntypedFormControl(false),
      requiresReceipt: new UntypedFormControl(false)
    });
    this.bindTypeDependency(this.formGroup);
    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);
    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(dataItem.name, Validators.required),
      type: new UntypedFormControl(dataItem.type, Validators.required),
      kmRate: new UntypedFormControl(dataItem.kmRate),
      flatrate: new UntypedFormControl(dataItem.flatrate),
      salaryPayment: new UntypedFormControl(dataItem.salaryPayment ?? false),
      requiresReceipt: new UntypedFormControl(dataItem.requiresReceipt ?? false)
    });
    this.bindTypeDependency(this.formGroup);
    this.editedRowIndex = rowIndex;
    sender.editRow(rowIndex, this.formGroup);
  }

  cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  saveHandler(event) {
    const value = event.formGroup.value;
    const isKm = value.type === ExpenseTypeKind.KmExpense;
    const request = {
      id: event.dataItem.id ?? null,
      name: value.name,
      type: value.type,
      kmRate: isKm ? value.kmRate : null,
      flatrate: !isKm ? value.flatrate : null,
      salaryPayment: value.salaryPayment ?? false,
      requiresReceipt: value.requiresReceipt ?? false
    };

    if (!event.dataItem.id) {
      this.expenseTypeService.createExpenseType(request).subscribe(() => this.load());
    } else {
      this.expenseTypeService.updateExpenseType(request).subscribe(() => this.load());
    }

    event.sender.closeRow(event.rowIndex);
  }

  deletionDialogOpened = false;
  private itemToDelete: { id: string } | null = null;

  removeHandler({ dataItem }) {
    this.itemToDelete = dataItem;
    this.deletionDialogOpened = true;
  }

  deleteConfirmed() {
    const dataItem = this.itemToDelete;
    this.itemToDelete = null;
    if (!dataItem) return;
    this.expenseTypeService.deleteExpenseType(dataItem.id).subscribe(() => this.load());
  }

  // kmRate is only relevant for Fahrspesen, flatrate only for Fester Betrag —
  // keep the irrelevant field disabled and react to changes of the selected type.
  private bindTypeDependency(formGroup: UntypedFormGroup) {
    this.typeChangeSub?.unsubscribe();
    this.syncFieldStates(formGroup);
    this.typeChangeSub = formGroup.get('type').valueChanges
      .subscribe(() => this.syncFieldStates(formGroup));
  }

  private syncFieldStates(formGroup: UntypedFormGroup) {
    const isKm = formGroup.get('type').value === ExpenseTypeKind.KmExpense;
    const kmRate = formGroup.get('kmRate');
    const flatrate = formGroup.get('flatrate');

    if (isKm) {
      kmRate.enable({ emitEvent: false });
      flatrate.disable({ emitEvent: false });
    } else {
      kmRate.disable({ emitEvent: false });
      flatrate.enable({ emitEvent: false });
    }
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.typeChangeSub?.unsubscribe();
    this.typeChangeSub = undefined;
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
